<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="600px"
      :transition="false"
      persistent
    >
      <v-card v-if="loading">
        <v-card-text class="mt-0 pt-3">
          Loading...
          <v-progress-linear
            indeterminate
            color="primary"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
      <v-card v-else>
        <v-card-title>Edit APOT Incident ({{ sequenceNumber }})</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-v-text-scrollable">
          <v-alert
              v-if="errorAlert"
              type="error"
              outlined
              transition="scale-transition"
              class="mt-2"
          >
            {{ errorMessage }}
          </v-alert>

          <v-layout wrap>
            <v-flex xs5>
              <v-text-field
                v-model="formData.firstName"
                label="Patient First Name"
                maxlength="15"
              />
            </v-flex>
            <v-flex
              xs6
              offset-1
            >
              <v-text-field
                v-model="formData.lastName"
                label="Patient Last Name"
                maxlength="25"
              />
            </v-flex>
            <v-flex xs5>
              <date-picker
                v-model="dateOfBirthFormat"
                label="Patient Date of Birth"
              />
            </v-flex>
            <v-flex
              xs6
              offset-1
            >
              <v-select
                v-model="formData.sex"
                :items="genderOptions"
                label="Patient Gender"
                clearable
              />
            </v-flex>

            <!-- Facility Name -->
            <v-flex xs12>
              <v-autocomplete
                  v-model="formData.facilityCode"
                  v-validate="{ required: true}"
                  :items="facilities"
                  label="Facility Code/Name"
                  name="facilityCode"
                  :error-messages="errors.collect('facilityCode')"
              />
            </v-flex>

            <!-- Incident Date: Date and Time -->
            <v-flex xs12>
              <date-time
                  v-model="formData.incidentDateTime"
                  v-validate="{ required: true}"
                  label="Incident Date"
                  name="incidentDateTime"
                  :error-messages="errors.collect('incidentDateTime')"
              />
            </v-flex>

            <!-- Arrived at Destination: Date and Time -->
            <v-flex xs12>
              <date-time
                  v-model="formData.arrivedAtDestination"
                  v-validate="{ required: true}"
                  label="Patient Arrived at Destination"
                  name="arrivedAtDestination"
                  :error-messages="errors.collect('arrivedAtDestination')"
              />
            </v-flex>

            <!-- Destination Patient Transfer of Care: Date and Time -->
            <v-flex xs12>
              <date-time
                  v-model="formData.destinationTransferOfCare"
                  v-validate="{ required: true}"
                  label="Destination Patient Transfer of Care"
                  name="destinationTransferOfCare"
                  :error-messages="errors.collect('destinationTransferOfCare')"
              />
            </v-flex>

            <!-- Signature Date/Time: Date and Time -->
            <v-flex xs12>
              <date-time
                  v-model="formData.signatureDateTime"
                  v-validate="{ required: true}"
                  label="Signature Date/Time"
                  name="signatureDateTime"
                  :error-messages="errors.collect('signatureDateTime')"
              />
            </v-flex>

            <!-- Unit Back in Service: Date and Time -->
            <v-flex xs12>
              <date-time
                  v-model="formData.unitBackInService"
                  v-validate="{ required: true}"
                  label="Unit Back in Service"
                  name="unitBackInService"
                  :error-messages="errors.collect('unitBackInService')"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            color="blue darken-1"
            @click="closeDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            text
            color="primary"
            :loading="saving"
            @click="saveIncident"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <save-reason-popup ref="saveReasonPopup"/>
  </div>
</template>

<script>
import moment from 'moment';
import apotIncidents from '@/api/apotIncidents';
import lookup from '@/api/lookup';
import DateTime from '@/components/ApotIncidents/DateTime';
import errorAlertMixins from '@/mixins/errorAlertMixins';
import SaveReasonPopup from '@/components/IncidentView/SaveReasonPopup';
import _ from 'lodash';
import DatePicker from '../DatePicker';

export default {
  name: 'EditApotIncidentForm',
  components: {
    SaveReasonPopup,
    DatePicker,
    DateTime,
  },
  mixins: [errorAlertMixins],
  data() {
    return {
      dialog: false,
      loading: false,
      saving: false,
      incidentId: null,
      sequenceNumber: null,
      facilities: null,
      formData: {
        firstName: null,
        lastName: null,
        dateOfBirth: null,
        sex: null,
        incidentDateTime: null,
        facilityCode: null,
        arrivedAtDestination: null,
        destinationTransferOfCare: null,
        signatureDateTime: null,
        unitBackInService: null,
      },
      genderOptions: [
        { text: 'Male', value: 'M' },
        { text: 'Female', value: 'F' },
        { text: 'Unknown', value: 'N' },
      ],
      dictionary: {
        attributes: {
          incidentDateTime: 'Incident Date',
          arrivedAtDestination: 'Patient Arrived at Destination',
          destinationTransferOfCare: 'Destination Patient Transfer of Care',
          signatureDateTime: 'Signature Date/Time',
          unitBackInService: 'Unit Back in Service',
          facilityCode: 'Facility Code/Name',
        },
      },
    };
  },
  mounted() {
    this.$validator.localize('en', this.dictionary);
  },
  computed: {
    // UnitBackInService Date - Date part (DatePicker)
    unitBackInServiceDate: {
      get() {
        return this.formData.unitBackInService
          ? moment(this.formData.signatureDateTime).format('YYYY-MM-DD')
          : null;
      },
      set(value) {
        if (value) {
          const time = this.formData.unitBackInService
            ? moment(this.formData.unitBackInService).format('HH:mm:ss')
            : '00:00:00';
          this.formData.unitBackInService = `${value} ${time}`;
        }
      },
    },
    // DateOfBirthFormat Date - Date part (DatePicker)
    dateOfBirthFormat: {
      get() {
        return this.formData.dateOfBirth
          ? moment(this.formData.dateOfBirth).format('YYYY-MM-DD')
          : null;
      },
      set(value) {
        if (value) {
          this.formData.dateOfBirth = value;
        }
      },
    },
  },
  methods: {
    /**
     * Show edit form with incident data.
     * @param {Object} incident - The incident data to edit
     */
    async show(incident) {
      this.dialog = true;
      if (!this.facilities) {
        this.loading = true;
        try {
          this.facilities = await lookup.fetchNemsisFacilities();
        } finally {
          this.loading = false;
        }
      }
      this.incidentId = incident.incidentId;
      this.sequenceNumber = incident.sequenceNumber;
      this.formData = {
        firstName: incident.firstName || null,
        lastName: incident.lastName || null,
        dateOfBirth: incident.dateOfBirth || null,
        sex: incident.sex || '',
        incidentDateTime: incident.incidentDateTime || null,
        facilityCode: incident.facilityCode || null,
        arrivedAtDestination: incident.arrivedAtDestination || null,
        destinationTransferOfCare: incident.destinationTransferOfCare || null,
        signatureDateTime: incident.signatureDateTime || null,
        unitBackInService: incident.unitBackInService || null,
      };
    },

    /**
     * Close the dialog.
     */
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },

    /**
     * Reset form data.
     */
    resetForm() {
      this.incidentId = null;
      this.formData = {
        firstName: null,
        lastName: null,
        dateOfBirth: null,
        sex: null,
        incidentDateTime: null,
        facilityCode: null,
        arrivedAtDestination: null,
        destinationTransferOfCare: null,
        signatureDateTime: null,
        unitBackInService: null,
      };
    },

    /**
     * Save the incident data.
     */
    async saveIncident() {
      this.hideError();
      await this.$validator.reset();
      if (!await this.$validator.validateAll()) {
        return;
      }

      this.$refs.saveReasonPopup.show(async (reason) => {
        try {
          this.saving = true;
          const apotData = _.cloneDeep(this.formData);
          apotData.reason = reason;
          await apotIncidents.saveApotIncident(this.incidentId, apotData);
          this.$emit('saved');
          this.closeDialog();
        } catch (e) {
          this.parseErrorResponse(e.response, true);
        } finally {
          this.saving = false;
        }
      });
    },
  },
};
</script>

<style scoped>
</style>
